'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import type { ConcreteChartInterval } from '@/lib/chart/types';
export interface RetainedChartHistory {
  readonly key: string;
  readonly requestKey?: string;
  readonly interval: ConcreteChartInterval | 'all';
  readonly property: string;
  readonly chartType: 'line' | 'ohlcv';
  readonly liveWindowSeconds?: string;
  readonly from?: string;
  readonly to?: string;
  readonly targetBars?: number;
}
const MAX_RETAINED_HISTORY = 3;
interface RetainedChartHistoryGroup extends RetainedChartHistory {
  readonly properties: readonly string[];
}

/** Retain subscription configurations only. Bucket data stays in the SDK cache. */
export function useRetainedChartHistory(scope, selected, presentationRequestKey) {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== scope) {
    t1 = {
      scope,
      selectedKey: null,
      entries: t0
    };
    $[1] = scope;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [previous, setPrevious] = useState(t1);
  const selectedKey = selected === null ? null : `${selected.key}|${selected.property}`;
  if (scope === previous.scope && selectedKey === previous.selectedKey) {
    return previous.entries;
  }
  let t2;
  if ($[3] !== presentationRequestKey || $[4] !== previous.entries || $[5] !== previous.scope || $[6] !== scope || $[7] !== selected) {
    const retained = scope === previous.scope ? previous.entries.filter(entry => (entry.liveWindowSeconds !== undefined || entry.interval === "all" && entry.to === undefined) && entry.key !== selected?.key) : [];
    const previousProperties = scope === previous.scope ? previous.entries.find(entry_0 => entry_0.key === selected?.key)?.properties ?? [] : [];
    const entries = selected === null ? retained : [...retained, {
      ...selected,
      properties: previousProperties.includes(selected.property) ? previousProperties : [...previousProperties, selected.property]
    }];
    const presented = presentationRequestKey === undefined ? undefined : entries.find(entry_1 => entry_1.requestKey === presentationRequestKey);
    t2 = entries.length <= MAX_RETAINED_HISTORY ? entries : presented === undefined ? entries.slice(-MAX_RETAINED_HISTORY) : [presented, ...entries.filter(entry_2 => entry_2 !== presented).slice(1 - MAX_RETAINED_HISTORY)];
    $[3] = presentationRequestKey;
    $[4] = previous.entries;
    $[5] = previous.scope;
    $[6] = scope;
    $[7] = selected;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const bounded = t2;
  setPrevious({
    scope,
    selectedKey,
    entries: bounded
  });
  return bounded;
}

/** Live seeds are tiny rolling windows and stay subscribed once first needed. */
export function useRetainedChartSeeds(scope, consensus, marketCap) {
  const $ = _c(8);
  let t0;
  if ($[0] !== consensus || $[1] !== marketCap || $[2] !== scope) {
    t0 = {
      scope,
      consensus,
      marketCap
    };
    $[0] = consensus;
    $[1] = marketCap;
    $[2] = scope;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const [previous, setPrevious] = useState(t0);
  const t1 = consensus || previous.scope === scope && previous.consensus;
  const t2 = marketCap || previous.scope === scope && previous.marketCap;
  let t3;
  if ($[4] !== scope || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      scope,
      consensus: t1,
      marketCap: t2
    };
    $[4] = scope;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const next = t3;
  if (previous.scope !== scope || previous.consensus !== next.consensus || previous.marketCap !== next.marketCap) {
    setPrevious(next);
    return next;
  }
  return previous;
}